import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { EditSheet, changedFields, toField } from '../ui/EditSheet';
import { Field, field } from '../create/Field';

/**
 * Fix what a job says.
 *
 * Only the description of the work — the title, the detail, the urgency. Not the customer
 * and not the address: visits, photos, notes and invoices already hang off this row, and
 * moving it would silently re-attribute work that really happened somewhere else. A job
 * booked against the wrong customer gets cancelled and re-booked, which leaves a trail.
 *
 * Status isn't here either. That belongs to the state machine and to the buttons that drive
 * it, so there is exactly one way a job's status can change.
 */
export function EditJobSheet({
  open,
  onClose,
  job,
}: {
  open: boolean;
  onClose: () => void;
  job: {
    id: string;
    title: string;
    description: string | null;
    priority: string;
  };
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();

  const original = {
    title: job.title,
    description: toField(job.description),
    priority: job.priority as 'normal' | 'urgent',
  };
  const [draft, setDraft] = useState(original);
  const set = (k: keyof typeof original) => (v: string) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const changed = changedFields(original, draft);
  const dirty = Object.keys(changed).length > 0;

  const save = useMutation(
    trpc.job.update.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries();
        onClose();
      },
    }),
  );

  return (
    <EditSheet
      open={open}
      onClose={onClose}
      title="Edit this job"
      dirty={dirty}
      saving={save.isPending}
      error={save.error}
      onSave={() => save.mutate({ jobId: job.id, ...changed, ...idem() })}
    >
      <Field label="What's the job">
        <input
          className={field}
          value={draft.title}
          onChange={(e) => set('title')(e.target.value)}
          autoFocus
        />
      </Field>
      <Field label="Detail">
        <textarea
          className="min-h-[90px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
          value={draft.description}
          onChange={(e) => set('description')(e.target.value)}
          placeholder="What they told you on the phone, what to bring, what you already know."
        />
      </Field>
      <Field label="Urgency">
        <select
          className={field}
          value={draft.priority}
          onChange={(e) => set('priority')(e.target.value)}
        >
          <option value="normal">Normal</option>
          <option value="urgent">Urgent</option>
        </select>
      </Field>
      <p className="text-xs text-muted-foreground">
        The customer and the address can't be changed here — too much hangs off them. If
        it was booked against the wrong one, cancel it and book it again.
      </p>
    </EditSheet>
  );
}
